/**
 * Parser and shape detection for analyze_topic tool results. Kept separate
 * from AnalyzeTopicOutput.tsx so the parser is unit-testable without React
 * fast-refresh complaining about non-component exports living next to
 * components.
 */

/**
 * Parsed shape of an analyze_topic TOON body. Numbers stay as their original
 * textual representation so 64-bit nanosecond timestamps survive intact (JS
 * Number truncates above 2^53). The renderer prints values verbatim.
 */
export interface ToonTable {
  columns: string[];
  rowCount: number;
  rows: (string | boolean | null)[][];
}

/**
 * A JSON value parsed from a `key: <json>` preamble line. Integers of 16 or
 * more digits, Unix-epoch ns instants among them, arrive as their digit
 * string, so none is rounded to the nearest double.
 */
export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type JsonObject = { [key: string]: JsonValue };

/**
 * The line between a truncated result's head and tail rows:
 * `... K rows omitted (t_from -> t_to, d=<ns>ns) ...`. The range and duration
 * are absent when the result has no time column or either block has no rows;
 * chats stored by earlier backends carry the range without the duration.
 */
export interface SeamInfo {
  omitted: number;
  from?: string;
  to?: string;
  durationNs?: string;
}

/**
 * Detection + parsed payload for whatever the chat UI received as
 * analyze_topic output. The detection rules are deliberately conservative —
 * any text that doesn't begin with the `topic_ids:` preamble falls through to
 * the plaintext branch so error strings (e.g. "Topic not found.") render as
 * authored.
 */
export type AnalyzeTopicPayload =
  | ({
      kind: "toon";
      topicIds: string[];
      /** The `data` block: every row, or the first rows of a truncated result. */
      table: ToonTable;
      /** The `data_tail` block: the last matching rows of a truncated result. */
      tailTable?: ToonTable;
      seam?: SeamInfo;
      /**
       * Unindented lines after the tables that are neither the seam nor a
       * block header, joined with newlines: the `values of "col" ...` digest
       * lines that chats stored by earlier backends end with.
       */
      trailingText?: string;
    } & ToonAnnotations)
  | { kind: "legacy-json"; topicIds: string[]; table: ToonTable }
  | { kind: "text"; text: string }
  | { kind: "unknown"; raw: unknown };

// `topic_ids: [...]`, then zero or more `key: value` annotation lines, then
// the TOON table. Annotation keys the parser doesn't know are ignored so the
// backend can add new preamble lines without breaking rendering.
const _TOON_PREAMBLE =
  /^topic_ids:\s*(\[[^\n]*\])\s*\n((?:[A-Za-z_]\w*:[^\n]*\n)*)\n*(data\[\d+\]\{[^\n]*\}:[\s\S]*)$/;
const _TOON_HEADER = /^(data|data_tail)\[(\d+)\]\{(.*)\}:\s*$/;
const _SEAM_LINE =
  /^\.\.\. (\d+) rows omitted(?: \((-?\d+) -> (-?\d+)(?:, d=(-?\d+)ns)?\))? \.\.\.$/;
const _ANNOTATION_LINE = /^([A-Za-z_]\w*):\s*(.*)$/;

interface ToonAnnotations {
  /**
   * Total rows the query matched when it exceeded the rows shown (the backend
   * omits the annotation when the result is complete).
   */
  totalMatchingRows?: number;
  /** Backend notes: the query's LIMIT, an empty result, a unit mismatch, etc. */
  note?: string;
  /** Window, matched and returned spans (see analyze_topic's `spans`). */
  spans?: JsonObject;
  /** Facts over every matched row, keyed by numeric column. */
  profile?: Record<string, JsonObject>;
  /** Numeric columns past the profile's column cap, left unprofiled. */
  profileOmitted?: string[];
  /** Every value of each low-cardinality column, with counts and times. */
  values?: Record<string, JsonObject[]>;
  shape?: ShapeFact;
}

/**
 * analyze_topic's `shape`: the bucket width, and per profiled column the
 * start/n/min/avg/max of each equal-time bucket over every matched row.
 */
export interface ShapeFact {
  bucketNs?: JsonValue;
  columns: Record<string, JsonObject[]>;
}

const _JSON_NUMBER = /-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?/y;

/**
 * Quote every integer literal of 16+ digits (`Number.MAX_SAFE_INTEGER` has
 * 16) outside a JSON string, so `JSON.parse` keeps it as its exact digit
 * string instead of rounding it to the nearest double.
 */
function _quoteLargeIntegers(json: string): string {
  let out = "";
  let inString = false;
  let i = 0;
  while (i < json.length) {
    const ch = json[i];
    if (inString) {
      out += ch;
      if (ch === "\\") {
        out += json[i + 1] ?? "";
        i += 2;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      i++;
      continue;
    }
    if (ch === '"') {
      inString = true;
      out += ch;
      i++;
      continue;
    }
    _JSON_NUMBER.lastIndex = i;
    const number = _JSON_NUMBER.exec(json);
    if (number !== null) {
      const token = number[0];
      const isLongInteger = /^-?\d{16,}$/.test(token);
      out += isLongInteger ? `"${token}"` : token;
      i += token.length;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

function _parseJson(text: string): JsonValue | undefined {
  try {
    return JSON.parse(_quoteLargeIntegers(text)) as JsonValue;
  } catch {
    return undefined;
  }
}

function _isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return _isPlainObject(value);
}

/** Keeps each array-valued column, dropping entries that are not objects. */
function _columnEntries(value: JsonObject): Record<string, JsonObject[]> {
  return Object.fromEntries(
    Object.entries(value).flatMap(([column, entries]) =>
      Array.isArray(entries) ? [[column, entries.filter(_isJsonObject)]] : [],
    ),
  );
}

function _parseAnnotations(block: string): ToonAnnotations {
  const annotations: ToonAnnotations = {};
  for (const line of block.split("\n")) {
    const match = _ANNOTATION_LINE.exec(line);
    if (!match) {
      continue;
    }
    const [, key, raw] = match;
    if (key === "total_matching_rows") {
      // A bare integer; chats stored by earlier backends append
      // "(showing first ...)".
      const n = Number.parseInt(raw, 10);
      if (!Number.isNaN(n)) {
        annotations.totalMatchingRows = n;
      }
      continue;
    }
    const parsed = _parseJson(raw);
    if (key === "note") {
      // The backend JSON-quotes the note; fall back to the raw text if it
      // ever arrives unquoted.
      annotations.note = typeof parsed === "string" ? parsed : raw;
    } else if (key === "spans") {
      if (_isJsonObject(parsed)) {
        annotations.spans = parsed;
      }
    } else if (key === "profile") {
      if (_isJsonObject(parsed)) {
        annotations.profile = Object.fromEntries(
          Object.entries(parsed).filter(
            (entry): entry is [string, JsonObject] => _isJsonObject(entry[1]),
          ),
        );
      }
    } else if (key === "profile_omitted") {
      if (Array.isArray(parsed)) {
        annotations.profileOmitted = parsed.map(String);
      }
    } else if (key === "values") {
      if (_isJsonObject(parsed)) {
        annotations.values = _columnEntries(parsed);
      }
    } else if (key === "shape") {
      if (_isJsonObject(parsed) && _isJsonObject(parsed.columns)) {
        annotations.shape = {
          bucketNs: parsed.bucket_ns,
          columns: _columnEntries(parsed.columns),
        };
      }
    }
  }
  return annotations;
}

function _isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Tokenize one TOON row body (no leading indent). Quoted strings honor
 * ``\"``, ``\\``, ``\n``, ``\r``, ``\t`` escapes; bare ``null``/``true``/
 * ``false`` collapse to the corresponding JS scalars; everything else is
 * preserved as its original text so numeric precision is never lost in
 * display.
 */
export function parseToonRow(line: string): (string | boolean | null)[] {
  const values: (string | boolean | null)[] = [];
  let i = 0;
  while (i <= line.length) {
    while (i < line.length && (line[i] === " " || line[i] === "\t")) {
      i++;
    }
    if (i >= line.length) {
      break;
    }

    if (line[i] === '"') {
      i++;
      let s = "";
      while (i < line.length) {
        const ch = line[i];
        if (ch === "\\" && i + 1 < line.length) {
          const next = line[i + 1];
          if (next === "n") {
            s += "\n";
          } else if (next === "r") {
            s += "\r";
          } else if (next === "t") {
            s += "\t";
          } else {
            s += next;
          }
          i += 2;
        } else if (ch === '"') {
          i++;
          break;
        } else {
          s += ch;
          i++;
        }
      }
      values.push(s);
    } else {
      let s = "";
      while (i < line.length && line[i] !== ",") {
        s += line[i];
        i++;
      }
      const trimmed = s.trim();
      if (trimmed === "null") {
        values.push(null);
      } else if (trimmed === "true") {
        values.push(true);
      } else if (trimmed === "false") {
        values.push(false);
      } else {
        values.push(trimmed);
      }
    }

    if (i < line.length && line[i] === ",") {
      i++;
    } else {
      break;
    }
  }
  return values;
}

/**
 * Parse the TOON block whose header is ``lines[start]``, returning the table
 * and the index of the first line after it. A block ends at the first
 * non-blank line that is not indented: the seam line, the ``data_tail``
 * header or a legacy trailing digest line. Returns ``null`` if the header
 * line doesn't match.
 */
function _parseToonBlock(
  lines: string[],
  start: number,
): { name: string; table: ToonTable; next: number } | null {
  const headerMatch = _TOON_HEADER.exec(lines[start] ?? "");
  if (!headerMatch) {
    return null;
  }
  const rowCount = Number.parseInt(headerMatch[2], 10);
  const columns = parseToonRow(headerMatch[3]).map((v) => {
    if (v === null) {
      return "";
    }
    if (typeof v === "boolean") {
      return String(v);
    }
    return v;
  });

  const rows: (string | boolean | null)[][] = [];
  let i = start + 1;
  for (; i < lines.length; i++) {
    const raw = lines[i];
    // Blank lines are skipped (defensive — the encoder never emits them, but
    // the LLM may forward a slightly mangled payload).
    if (raw.trim().length === 0) {
      continue;
    }
    if (!/^\s/.test(raw)) {
      break;
    }
    rows.push(parseToonRow(raw.replace(/^\s+/, "")));
  }
  return { name: headerMatch[1], table: { columns, rowCount, rows }, next: i };
}

/**
 * Parse a TOON ``data[N]{cols}:\n  row...`` block. Returns ``null`` if the
 * header line doesn't match — callers treat that as "not TOON, fall back".
 */
export function parseToon(toonText: string): ToonTable | null {
  return _parseToonBlock(toonText.split("\n"), 0)?.table ?? null;
}

interface ToonBody {
  table: ToonTable;
  tailTable?: ToonTable;
  seam?: SeamInfo;
  trailingText?: string;
}

/**
 * Parse the body after the preamble: the ``data`` block, then optionally the
 * seam line and the ``data_tail`` block. Any other non-blank unindented line
 * (the ``values of "col" ...`` digest that chats stored by earlier backends
 * end with) is kept as ``trailingText``, so an old chat loses nothing.
 */
function _parseBody(bodyText: string): ToonBody | null {
  const lines = bodyText.split("\n");
  const head = _parseToonBlock(lines, 0);
  if (!head) {
    return null;
  }
  const body: ToonBody = { table: head.table };
  const trailing: string[] = [];
  let i = head.next;
  while (i < lines.length) {
    const seamMatch = _SEAM_LINE.exec(lines[i]);
    if (seamMatch) {
      const [, omitted, from, to, durationNs] = seamMatch;
      body.seam = {
        omitted: Number.parseInt(omitted, 10),
        ...(from !== undefined && { from }),
        ...(to !== undefined && { to }),
        ...(durationNs !== undefined && { durationNs }),
      };
      i++;
      continue;
    }
    const tail = _parseToonBlock(lines, i);
    if (tail?.name === "data_tail") {
      body.tailTable = tail.table;
      i = tail.next;
      continue;
    }
    if (lines[i].trim().length > 0) {
      trailing.push(lines[i]);
    }
    i++;
  }
  if (trailing.length > 0) {
    body.trailingText = trailing.join("\n");
  }
  return body;
}

/**
 * Coerce a legacy JSON ``data`` array (list of row objects) into the same
 * shape ``parseToon`` produces. Preserves existing-chat rendering while the
 * new TOON path is the default for fresh queries.
 */
function _legacyDataToTable(data: unknown[]): ToonTable {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of data) {
    if (_isPlainObject(row)) {
      for (const key of Object.keys(row)) {
        if (!seen.has(key)) {
          seen.add(key);
          columns.push(key);
        }
      }
    }
  }
  const rows: (string | boolean | null)[][] = data.map((row) => {
    if (!_isPlainObject(row)) {
      return columns.map(() => null);
    }
    return columns.map((col) => {
      const v = row[col];
      if (v === null || v === undefined) {
        return null;
      }
      if (typeof v === "boolean") {
        return v;
      }
      if (typeof v === "number" || typeof v === "bigint") {
        return String(v);
      }
      if (typeof v === "string") {
        return v;
      }
      return JSON.stringify(v);
    });
  });
  return { columns, rowCount: rows.length, rows };
}

/**
 * Decide what kind of analyze_topic payload we have so the renderer can pick
 * a layout. The string branch eats any leading whitespace before the
 * ``topic_ids:`` preamble so the LLM can't break detection by reflowing the
 * payload through an intermediate format.
 */
export function detectAnalyzeTopicPayload(
  output: unknown,
): AnalyzeTopicPayload {
  if (typeof output === "string") {
    const match = _TOON_PREAMBLE.exec(output.trim());
    if (match) {
      let topicIds: unknown;
      try {
        topicIds = JSON.parse(match[1]);
      } catch {
        return { kind: "text", text: output };
      }
      if (
        !Array.isArray(topicIds) ||
        !topicIds.every((id) => typeof id === "string")
      ) {
        return { kind: "text", text: output };
      }
      const body = _parseBody(match[3]);
      if (!body) {
        return { kind: "text", text: output };
      }
      return {
        kind: "toon",
        topicIds,
        ...body,
        ..._parseAnnotations(match[2]),
      };
    }
    return { kind: "text", text: output };
  }

  if (_isPlainObject(output)) {
    // ``extractOutput`` in ToolDetailPanel leaves ``{json: ...}`` content
    // blocks intact when the response isn't a text block; unwrap once so
    // legacy ``{topic_ids, data}`` payloads from pre-TOON chats are detected.
    const unwrapped = _isPlainObject(output.json) ? output.json : output;
    const topicIds = unwrapped.topic_ids;
    const data = unwrapped.data;
    if (
      Array.isArray(topicIds) &&
      topicIds.every((id) => typeof id === "string") &&
      Array.isArray(data)
    ) {
      return {
        kind: "legacy-json",
        topicIds,
        table: _legacyDataToTable(data),
      };
    }
  }

  return { kind: "unknown", raw: output };
}
